// Private dashboard: which floating WhatsApp button variant converts best.
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const ADMIN_TOKEN_KEY = "cta-clicks:admin-token:v1";

function getAdminToken(): string {
  if (typeof window === "undefined") return "";
  let token = window.sessionStorage.getItem(ADMIN_TOKEN_KEY) ?? "";
  if (!token) {
    token = window.prompt("Enter ADMIN_TOKEN to view the A/B report") ?? "";
    if (token) window.sessionStorage.setItem(ADMIN_TOKEN_KEY, token);
  }
  return token;
}

interface Row {
  variant: string;
  label: string;
  exposures: number;
  whatsappClicks: number;
  anyCtaClicks: number;
  leads: number;
  whatsappRate: number;
  leadRate: number;
  liftVsControl: number | null;
  confidence: number | null;
  significant: boolean;
}

interface Payload {
  days: number;
  generatedAt: string;
  rows: Row[];
  totalExposures: number;
  byDevice: Array<{ device: string; rows: Row[]; totalExposures: number }>;
  winner: string | null;
  verdict: string;
}

export const Route = createFileRoute("/admin/ab-whatsapp")({
  head: () => ({
    meta: [
      { title: "WhatsApp Button A/B Test | Arif Khan" },
      { name: "robots", content: "noindex,nofollow" },
      {
        name: "description",
        content: "Private experiment report for the floating WhatsApp button.",
      },
    ],
  }),
  component: AbDashboard,
});

function AbDashboard() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState<Payload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (d: number) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/admin/ab-whatsapp?days=${d}`, {
        headers: { "x-admin-token": getAdminToken() },
        cache: "no-store",
      });
      if (!res.ok) {
        if (res.status === 401)
          window.sessionStorage.removeItem(ADMIN_TOKEN_KEY);
        throw new Error(`Request failed (${res.status})`);
      }
      setData((await res.json()) as Payload);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(days);
  }, [days, load]);

  return (
    <main className="min-h-screen bg-background px-6 py-16">
      <div className="mx-auto max-w-6xl space-y-8">
        <header className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight">
              WhatsApp Button A/B Test
            </h1>
            <p className="mt-2 text-sm text-ink-soft">
              Three versions of the floating button — icon only (bottom right),
              a labelled pill, and a chat icon on the bottom left — split evenly
              across visitors.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {[7, 30, 90].map((d) => (
              <Button
                key={d}
                variant={d === days ? "default" : "outline"}
                size="sm"
                onClick={() => setDays(d)}
              >
                {d}d
              </Button>
            ))}
            <Button size="sm" variant="ghost" onClick={() => void load(days)}>
              Refresh
            </Button>
          </div>
        </header>

        {error && (
          <p className="text-sm text-red-500" role="alert">
            {error}
          </p>
        )}
        {loading && !data && <p className="text-sm text-ink-soft">Loading…</p>}

        {data && (
          <>
            <Card
              className={
                data.winner && data.winner !== "control"
                  ? "border-emerald-500/40"
                  : undefined
              }
            >
              <CardHeader>
                <CardTitle>Recommendation</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <p className="text-base">{data.verdict}</p>
                <p className="text-xs text-ink-soft">
                  {data.totalExposures.toLocaleString()} visitor views in the
                  last {data.days} days · updated{" "}
                  {new Date(data.generatedAt).toLocaleString()}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>WhatsApp tap rate by variant</CardTitle>
              </CardHeader>
              <CardContent className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.rows}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                    <XAxis dataKey="variant" />
                    <YAxis unit="%" />
                    <Tooltip formatter={(v: number) => `${v}%`} />
                    <Legend />
                    <Bar
                      dataKey="whatsappRate"
                      name="WhatsApp taps"
                      fill="#25D366"
                      radius={[4, 4, 0, 0]}
                    />
                    <Bar
                      dataKey="leadRate"
                      name="Enquiries"
                      fill="#0ea5e9"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Results</CardTitle>
              </CardHeader>
              <CardContent className="overflow-x-auto">
                <ResultsTable rows={data.rows} />
              </CardContent>
            </Card>

            {data.byDevice
              .filter((d) => d.totalExposures > 0)
              .map((d) => (
                <Card key={d.device}>
                  <CardHeader>
                    <CardTitle className="capitalize">
                      {d.device} visitors
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="overflow-x-auto">
                    <ResultsTable rows={d.rows} />
                  </CardContent>
                </Card>
              ))}
          </>
        )}
      </div>
    </main>
  );
}

function ResultsTable({ rows }: { rows: Row[] }) {
  return (
    <table className="w-full min-w-[720px] text-sm">
      <thead className="text-left text-ink-soft">
        <tr className="border-b">
          <th className="py-2 pr-4">Variant</th>
          <th className="py-2 pr-4">Views</th>
          <th className="py-2 pr-4">WhatsApp taps</th>
          <th className="py-2 pr-4">Tap rate</th>
          <th className="py-2 pr-4">Enquiries</th>
          <th className="py-2 pr-4">Enquiry rate</th>
          <th className="py-2 pr-4">Lift vs original</th>
          <th className="py-2">Confidence</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.variant} className="border-b last:border-0">
            <td className="py-2 pr-4">
              <span className="font-medium">{r.variant}</span>
              <span className="block text-xs text-ink-soft">{r.label}</span>
            </td>
            <td className="py-2 pr-4">{r.exposures.toLocaleString()}</td>
            <td className="py-2 pr-4">{r.whatsappClicks.toLocaleString()}</td>
            <td className="py-2 pr-4 font-medium">{r.whatsappRate}%</td>
            <td className="py-2 pr-4">{r.leads.toLocaleString()}</td>
            <td className="py-2 pr-4">{r.leadRate}%</td>
            <td
              className={`py-2 pr-4 ${
                (r.liftVsControl ?? 0) > 0
                  ? "text-emerald-600"
                  : (r.liftVsControl ?? 0) < 0
                    ? "text-red-500"
                    : ""
              }`}
            >
              {r.variant === "control"
                ? "—"
                : r.liftVsControl === null
                  ? "n/a"
                  : `${r.liftVsControl > 0 ? "+" : ""}${r.liftVsControl}%`}
            </td>
            <td className="py-2">
              {r.variant === "control"
                ? "baseline"
                : r.confidence === null
                  ? "n/a"
                  : `${r.confidence}%${r.significant ? " ✓" : ""}`}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
